import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { typography, radius } from '@/shared/theme/tokens';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { useLiveModeModel } from '../../hooks/useLiveModeModel';
import { BOARD, BOARD_KEYFRAMES } from './boardTheme';
import { BoardBanner } from './BoardBanner';
import { RecBoard } from './RecBoard';
import { AlertBoard } from './AlertBoard';

/**
 * Industrial signage / departures-board variant of Live Mode.
 *
 * Reads as an airport departures board / stadium scoreboard hung on a control-room
 * wall: one giant signage banner driven by the adaptive headline, then two
 * board sections — recommendations and the monitor — with split-flap counters for
 * every value and setpoint move. Maximal legibility, minimal chrome. Same shared
 * model (data + IA) as every variant; only the visual language differs.
 *
 * Read-only: a rec row calls onSelectRec, an alert row calls onSelectAlert.
 */
export function BoardScreen({ deploymentName, seedAlerts, seedRecs, isLoading, isError, onSelectRec, onSelectAlert }) {
  const { isDesktop } = useBreakpoint();
  const {
    alerts, recs, clock, flashId, jitterActive,
    degraded, rankedRecs, rankedAlerts,
    headline, freshness, animate,
  } = useLiveModeModel(seedAlerts, seedRecs);

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
      background: BOARD.wall, color: BOARD.ink, fontFamily: typography.fontFamily.body,
    }}>
      <style>{BOARD_KEYFRAMES}</style>

      <div style={{
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        border: `1px solid ${BOARD.rule}`, borderRadius: radius.xs, overflow: 'hidden',
      }}>
        <BoardBanner
          headline={headline}
          deploymentName={deploymentName}
          clock={clock}
          freshness={freshness}
          jitterActive={jitterActive}
          animate={animate}
        />

        {isError ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: BOARD.board }}>
            <ErrorState message="SIGNAL LOST — couldn't load live data for this board." />
          </div>
        ) : isLoading && alerts.length === 0 && recs.length === 0 ? (
          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: isDesktop ? '1.25fr 1fr' : '1fr', gap: 1, background: BOARD.seam }}>
            <div style={{ background: BOARD.board, padding: 16 }}><SkeletonCard rows={6} height="100%" /></div>
            <div style={{ background: BOARD.board, padding: 16 }}><SkeletonCard rows={6} height="100%" /></div>
          </div>
        ) : (
          <div style={{
            flex: 1, minHeight: 0,
            display: isDesktop ? 'grid' : 'flex', flexDirection: isDesktop ? undefined : 'column',
            gridTemplateColumns: isDesktop ? '1.25fr 1fr' : undefined,
            gap: 1, background: BOARD.seam, overflowY: isDesktop ? undefined : 'auto',
          }}>
            <div style={{ minWidth: 0, minHeight: 0, display: 'flex' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <RecBoard recs={rankedRecs} paused={degraded} animate={animate} onSelect={onSelectRec} />
              </div>
            </div>
            <div style={{ minWidth: 0, minHeight: 0, display: 'flex' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AlertBoard alerts={rankedAlerts} flashId={flashId} animate={animate} onSelect={onSelectAlert} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
